import {
    Badge,
    Box,
    Button,
    Grid,
    GridItem,
    HStack,
    Separator,
    Spinner,
    Stack,
    Table,
    Tag,
    Text,
    useDisclosure
} from "@chakra-ui/react";
import {UI} from "components";
import {API, Mask, Styles, toaster} from "config";
import useOrder from "hooks/useOrder";
import moment from "moment";
import {useState} from "react";
import {useParams} from "react-router";
import {useQueryClient} from "@tanstack/react-query";
import Approve from "./approve";
import Cancel from "./cancel";
import History from "./history";
import Attach from "./attach";
import Done from "./done";
import Pix from "./pix";
import useDistributor from "hooks/useDistributor";

export default function Buy() {

    const params = useParams()
    const order = useOrder(params.id)
    const status = Styles.orderStatus(order.data?.status)
    const distributor = useDistributor()

    const approve = useDisclosure()
    const cancel = useDisclosure()
    const history = useDisclosure()
    const done = useDisclosure()

    // Sub-pedidos gerados por segmentação de categoria (compra da Matriz)
    const segmentedOrders = order.data?.segmented_orders ?? []
    const isSegmented = segmentedOrders.length > 0
    // Lista unificada: pedido principal + sub-pedidos para exibir pagamentos separados
    const allOrdersForPayment = isSegmented ? [order.data, ...segmentedOrders] : [order.data]

    const tableItensColumns = [
        {children: 'Produto'},
        {children: 'Qtd', textAlign: 'center'},
        {children: 'Qtd Atendida', textAlign: 'center'},
        {children: 'Und (R$)'},
        {children: 'Total'}
    ]
    const tableTransactionsColumns = [
        {children: 'Método'},
        {children: 'Valor'},
        {children: 'Status'},
        {children: ''}
    ]

    return (
        <>
            <Approve data={order.data} {...approve} />
            <Cancel data={order.data} {...cancel} />
            <History data={order.data} {...history} />
            <Done data={order.data} {...done} />

            <UI.Header title={order?.data ? `Compra #${order.data?.cod}` : `Compra #${params?.id}`}/>

            <UI.Show visible={order.isFetching}>
                <UI.Alert my={4} size="sm" colorPalette="gray" indicator={<Spinner/>}>
                    Obtendo dados do pedido...
                </UI.Alert>
            </UI.Show>

            <Stack mb={4}>
                <UI.Show visible={order.data?.status === 11}>
                    <UI.Alert colorPalette="green" size="sm" title="Disponível para retirada na Matriz">Seu pedido já
                        foi processado e já está disponível para ser retirado pessoalmente na Matriz.
                    </UI.Alert>
                </UI.Show>
                <UI.Show visible={order.data?.status === 9}>
                    <UI.Alert colorPalette="green" size="sm" title="🚚 Pedido em trânsito">Seu pedido já foi despachado e
                        logo chegará até você. Para adicionar os produtos no seu estoque, conclua o pedido no ato do
                        recebimento.
                    </UI.Alert>
                </UI.Show>
                <UI.Show visible={order.data?.status === 5}>
                    <UI.Alert size="sm" colorPalette="red" title="Pedido cancelado">Este pedido foi
                        cancelado {moment(order.data?.canceled_at).fromNow()}. Verifique detalhes do pedido para mais
                        informações.
                    </UI.Alert>
                </UI.Show>
                <UI.Show visible={order.data?.status === 12}>
                    <UI.Alert size="sm" colorPalette="orange" title="Pagamentos em análise">Um ou mais pagamentos do seu
                        pedido estão em análise. <b>Anexe o comprovante de pagamento</b> o quanto antes para evitar o
                        cancelamento automático do seu pedido.
                    </UI.Alert>
                </UI.Show>
            </Stack>

            <Grid templateColumns={{base: '1fr', sm: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)'}} gap={6}>

                <GridItem>
                    <UI.Box bg="transparent" borderWidth={1} borderStyle="dashed">
                        <Stack gap={3}>
                            <Text fontSize="sm" color="GrayText">Informações do pedido:</Text>

                            <HStack flexWrap="wrap">
                                {order.data?.type === 3 ? <Badge size="lg" colorPalette="purple" children="Adesão"/> :
                                    <Badge size="lg" colorPalette="gray" children="Produtos"/>}
                                <Badge size="lg" colorPalette={status.colorPalette}><UI.Icon
                                    name={status.icon}/> {status.label}
                                </Badge>
                            </HStack>

                            {/* Quando segmentado: exibe todos os códigos gerados */}
                            <UI.Show visible={isSegmented}>
                                <Box>
                                    <Text fontSize="sm" color="GrayText" mb={1}>Pedidos gerados:</Text>
                                    <HStack flexWrap="wrap" gap={1}>
                                        {allOrdersForPayment.map(o => (
                                            <Badge key={o?.id} colorPalette="orange" size="md">{o?.cod}</Badge>
                                        ))}
                                    </HStack>
                                    <Text fontSize="xs" color="GrayText" mt={1}>
                                        Sua compra foi separada em {allOrdersForPayment.length} pedidos por categoria de produto.
                                    </Text>
                                </Box>
                            </UI.Show>

                            <Box>
                                <Text fontSize="sm" color="GrayText">Fornecedor:</Text>
                                <Text fontSize="sm" fontWeight="bold">{order.data?.distributor_from?.name ?? '—'}</Text>
                            </Box>

                            <Box>
                                <Text fontSize="sm" color="GrayText">{isSegmented ? 'Total deste pedido:' : 'Total do pedido:'}</Text>
                                <Text fontSize="md" fontWeight="bold"><UI.Number.Amount
                                    value={order.data?.total_amount}/>
                                </Text>
                            </Box>

                            <Box>
                                <Text fontSize="sm" color="GrayText">Data do pedido:</Text>
                                <Text fontSize="sm">{moment(order?.data?.created_at).format('LLL')}</Text>
                            </Box>

                            <Box>
                                <Text fontSize="sm" color="GrayText">Última atualização:</Text>
                                <Text fontSize="sm">{moment(order?.data?.created_at).fromNow()}</Text>
                            </Box>

                            <UI.Show visible={order.data?.status === 5}>
                                <Separator/>

                                <Box>
                                    <Text fontSize="sm" color="GrayText">Cancelado em:</Text>
                                    <Text fontSize="sm">{moment(order?.data?.canceled_at).format('LLL')}</Text>
                                </Box>

                                <Box>
                                    <Text fontSize="sm" color="GrayText">Motivo do cancelamento:</Text>
                                    <Text fontSize="sm">{order?.data?.canceled_reason ?? <>&mdash;</>}</Text>
                                </Box>
                            </UI.Show>

                            <Separator/>

                            <Stack gap={1}>
                                 <UI.Show visible={order.data?.status === 12}>
                                     <Button variant="subtle" colorPalette="red" size="xs" onClick={cancel.onOpen}>
                                         <UI.Icon name="cancel" /> Cancelar pedido
                                     </Button>
                                 </UI.Show>
                                <UI.Show visible={order.data?.status === 9 || order.data?.status === 11}>
                                    <Button variant="subtle" colorPalette="teal" size="xs" onClick={done.onOpen}>
                                        <UI.Icon name="done"/> Concluir pedido
                                    </Button>
                                </UI.Show>
                            </Stack>
                        </Stack>
                    </UI.Box>
                </GridItem>

                {isSegmented ? (
                    /* Cards unificados (itens + pagamentos por pedido) — ocupa 2 linhas do grid */
                    <GridItem colSpan={{base: 2, sm: 2, md: 1, lg: 2}} rowSpan={2}>
                        <Stack gap={5}>
                            {allOrdersForPayment.map((o, index) => {
                                const isMain = index === 0
                                const oStatus = Styles.orderStatus(o?.status)
                                return (
                                    <Box
                                        key={o?.id ?? index}
                                        borderWidth={1}
                                        borderRadius="xl"
                                        overflow="hidden"
                                    >
                                        {/* Cabeçalho do card */}
                                        <HStack
                                            px={5} py={4}
                                            borderBottomWidth={1}
                                            borderLeftWidth={4}
                                            borderLeftColor="orange.500"
                                            justify="space-between"
                                            flexWrap="wrap"
                                            gap={3}
                                        >
                                            <HStack gap={3} alignItems="center" flexWrap="wrap">
                                                <Badge colorPalette="orange" size="lg" fontWeight="bold">
                                                    {o?.cod}
                                                </Badge>
                                                <Text fontWeight="semibold" fontSize="sm">
                                                    {isMain ? 'Pedido Principal' : 'Sub-pedido'}
                                                </Text>
                                                <Badge colorPalette={oStatus.colorPalette} size="sm">
                                                    <UI.Icon name={oStatus.icon}/> {oStatus.label}
                                                </Badge>
                                            </HStack>
                                            <Text fontWeight="bold" fontSize="md" color="orange.fg">
                                                <UI.Number.Amount value={o?.total_amount}/>
                                            </Text>
                                        </HStack>

                                        {/* Itens */}
                                        <Box px={4} pt={4} pb={3}>
                                            <Text
                                                fontSize="xs"
                                                fontWeight="semibold"
                                                color="GrayText"
                                                textTransform="uppercase"
                                                letterSpacing="wider"
                                                mb={3}
                                            >
                                                Itens
                                            </Text>
                                            <UI.Table
                                                variant="outline"
                                                columns={tableItensColumns}
                                                data={o?.itens}
                                                Row={Row}
                                            />
                                            <HStack justify="flex-end" mt={2} gap={2}>
                                                <Text fontSize="xs" color="GrayText">Subtotal dos itens:</Text>
                                                <Text fontSize="sm" fontWeight="bold">
                                                    R$ {Mask.price(o?.itens_amount)}
                                                </Text>
                                            </HStack>
                                        </Box>

                                        <Separator/>

                                        {/* Pagamentos */}
                                        <Box px={4} py={4}>
                                            <Text
                                                fontSize="xs"
                                                fontWeight="semibold"
                                                color="GrayText"
                                                textTransform="uppercase"
                                                letterSpacing="wider"
                                                mb={3}
                                            >
                                                Pagamentos
                                            </Text>
                                            <UI.Table
                                                variant="outline"
                                                columns={tableTransactionsColumns}
                                                data={o?.transactions}
                                                Row={(props) => (
                                                    <RowTransaction
                                                        {...props}
                                                        distributor={distributor}
                                                        orderData={o}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Box>
                                )
                            })}
                        </Stack>
                    </GridItem>
                ) : (
                    /* Layout normal — só itens, pagamentos ficam no GridItem abaixo */
                    <GridItem colSpan={{base: 2, sm: 2, md: 1, lg: 2}}>
                        <UI.Box bg="transparent" p={0}>
                            <Stack gap={3}>
                                <Text fontSize="sm" color="GrayText">Itens do pedido:</Text>
                                <UI.Table variant="outline" columns={tableItensColumns} data={order.data?.itens}
                                          meta={order.data?.meta} Row={Row}/>
                                <Box>
                                    <Text fontSize="xs" color="GrayText">Total dos itens:</Text>
                                    <Text fontSize="lg" fontWeight="bold">R$ {Mask.price(order.data?.itens_amount)}</Text>
                                </Box>
                            </Stack>
                        </UI.Box>
                    </GridItem>
                )}

                <GridItem>
                    <UI.Box bg="transparent" borderWidth={1} borderStyle="dashed">
                        <Stack gap={3}>
                            <Text fontSize="sm" color="GrayText">Informações da entrega:</Text>

                            {order.data?.shipping_type === 1 ?
                                <Badge size="lg" colorPalette="blue" children="🚚 Para envio"/> :
                                <Badge size="lg" colorPalette="gray" children="Retirada na Matriz"/>}

                            <UI.Show visible={order.data?.shipping_type === 2}>

                                <Box>
                                    <Text fontSize="sm" color="GrayText">Local de retirada:</Text>
                                    <Text fontSize="sm">Matriz Goiânia (Expedição)</Text>
                                </Box>

                                <Box>
                                    <Text fontSize="sm" color="GrayText">Endereço</Text>
                                    <Text fontSize="sm">Av. BANDEIRANTES, 1273 QD.18 LT.25, JARDIM PETROPOLIS, CEP
                                        74.460-190 GOIANIA (GO)</Text>
                                </Box>

                                {/* <Button size="xs" variant="subtle"><UI.Icon name="directions" /> Obter direções</Button> */}

                            </UI.Show>

                            <UI.Show visible={order.data?.shipping_type === 1}>

                                <Box>
                                    <Text fontSize="sm" color="GrayText">Endereço para entrega:</Text>
                                    <Text fontSize="sm"
                                          textTransform="capitalize">{distributor.data?.city?.name}, {distributor.data?.city?.state_code}, {distributor.data?.city?.country_code} | {distributor.data?.address_line_1} {distributor.data?.address_line_2} | {distributor.data?.address_number} | {distributor.data?.address_complement}, {distributor.data?.zip}</Text>
                                </Box>

                                <Box>
                                    <Text fontSize="sm" color="GrayText">Transportadora:</Text>
                                    <Text fontSize="md">{order.data?.shipment?.label}</Text>
                                </Box>

                                <Box>
                                    <Text fontSize="sm" color="GrayText">Código de rastreio:</Text>
                                    <Text fontSize="sm"
                                          textTransform="uppercase">{order.data?.shipping_track_code}</Text>
                                </Box>

                                <Box>
                                    <Text fontSize="sm" color="GrayText">Valor do envio:</Text>
                                    <Text fontSize="sm"><UI.Number.Amount value={order.data?.shipping_amount}/></Text>
                                </Box>

                            </UI.Show>

                            <UI.Show visible={!!order.data?.admin_comments}>
                                <Box>
                                    <Text fontSize="sm" color="GrayText">Observações:</Text>
                                    <Text fontSize="sm">{order.data?.admin_comments}:</Text>
                                </Box>
                            </UI.Show>

                        </Stack>
                    </UI.Box>
                </GridItem>

                {!isSegmented && (
                    <GridItem colSpan={{base: 2, sm: 2, md: 1, lg: 2}}>
                        <UI.Box bg="transparent" p={0}>
                            <Stack gap={3}>
                                <Text fontSize="sm" color="GrayText">Pagamentos:</Text>
                                <UI.Table
                                    variant="outline"
                                    columns={tableTransactionsColumns}
                                    data={order.data?.transactions}
                                    Row={(props) => (
                                        <RowTransaction
                                            {...props}
                                            distributor={distributor}
                                            orderData={order.data}
                                        />
                                    )}
                                />
                            </Stack>
                        </UI.Box>
                    </GridItem>
                )}

            </Grid>
        </>
    )
}

const Row = ({data}) => {

    const defaultPrice = data?.product?.distributor_price ?? data?.product_kit?.price_distributor
    const defaultDiscount = data?.product?.distributor_discount

    return (
        <Table.Row>
            <Table.Cell fontSize="xs"><UI.Tag.Avatar size="lg" name={data?.product?.name ?? data?.product_kit?.label}
                                                     src={data?.product?.image_url}/></Table.Cell>
            <Table.Cell fontSize="xs" textAlign="center"><Tag.Root
                colorPalette="orange"><Tag.Label>{data?.quantity}</Tag.Label></Tag.Root></Table.Cell>
            <Table.Cell fontSize="xs" textAlign="center"><Tag.Root
                colorPalette="orange"><Tag.Label>{data?.shipped_quantity ?? data?.quantity} </Tag.Label></Tag.Root></Table.Cell>
            <Table.Cell fontSize="xs">R$ {Mask.price(data?.price_und ?? defaultPrice)} <UI.Show
                visible={!!data?.product}><Badge
                size="sm">- {data?.discount_percentage ?? defaultDiscount}%</Badge></UI.Show></Table.Cell>
            <Table.Cell fontSize="xs">R$ {Mask.price(data?.amount)}</Table.Cell>
        </Table.Row>
    )
}

const RowTransaction = ({data, distributor, modalAttachOpen, orderData}) => {
    const params = useParams()
    const queryClient = useQueryClient()
    const order = useOrder(params.id)
    const [deleting, setDeleting] = useState(false)
    const status = Styles.transactionStatus(data?.status)
    // TEMP: método 9 (Cielo) desabilitado — descomentar quando reativar
    // const cielo_status = Styles.cieloStatus(Number(data?.transaction_checkout_data?.in?.payment_status))
    // const cielo_label = Styles.cieloLabel(Number(data?.transaction_checkout_data?.in?.payment_status))
    const method = Styles.transactionMethod(data?.method)
    const attach = useDisclosure()
    const pix = useDisclosure()

    const hasReceipt = !!data?.receipt
    // const isPix = data?.method === 4 // TEMP: método 9 (Cielo) desabilitado — restaurar esta linha e remover a de baixo
    const isPix = data?.method === 4 || data?.method === 9
    const isPending = data?.status === 3

    // orderData é passado quando exibindo sub-pedidos segmentados; caso contrário usa o pedido do hook
    const currentOrder = orderData ?? order.data
    const isMatriz = currentOrder?.distributor_from?.is_matriz
    const whatsappNumber = isMatriz
        ? '5562994020478'
        : '5516997632620';

    const invalidateOrder = () => queryClient.invalidateQueries({ queryKey: ['order', params.id] })

    const handleDeleteReceipt = async () => {
        if (!window.confirm('Tem certeza que deseja excluir o comprovante?')) return
        setDeleting(true)
        try {
            await API.delete(`/transactions/${data.id}/receipt`)
            invalidateOrder()
        } catch (err) {
            toaster.create({ title: 'Não foi possível excluir o comprovante', description: err?.response?.data?.message, type: 'warning' })
        }
        setDeleting(false)
    }

    return (
        <>
            <Table.Row>
                <Table.Cell fontSize="xs"><UI.Icon name={method.icon}/> {data?.method_label}</Table.Cell>
                <Table.Cell fontSize="xs" fontWeight="bold">R$ {Mask.price(data?.amount)}</Table.Cell>
                <Table.Cell fontSize="xs">
                    <HStack>
                        <Badge colorPalette={status.colorPalette}>{data?.status_label}</Badge>
                        {/* TEMP: método 9 (Cielo) desabilitado — descomentar quando reativar
                        <UI.Show visible={data?.method === 9 && isPending}>
                            <Badge colorPalette={cielo_status?.colorPalette}>
                                ( Cielo: {cielo_label?.label} )
                            </Badge>
                        </UI.Show>
                        */}
                    </HStack>
                </Table.Cell>
                <Table.Cell fontSize="xs" textAlign="right">
                    <HStack w="100%" justifyContent="flex-end">

                        {/* TEMP: método 9 (Cielo) desabilitado — descomentar quando reativar
                        <UI.Show visible={data?.method === 9 && isPending}>
                            <Button size="xs" colorPalette="orange" onClick={() => window.open(data.payment_url)}>
                                <UI.Icon name="cielo"/> Pagar agora <UI.Icon name="next"/>
                            </Button>
                        </UI.Show>
                        */}

                        <UI.Show visible={isPix && isPending}>
                            <Button size="xs" colorPalette="orange" disabled={hasReceipt} onClick={pix.onOpen}>
                                <UI.Icon name="pix"/> Pagar com Pix
                            </Button>
                        </UI.Show>

                        <UI.Show visible={isPix && isPending && !hasReceipt}>
                            <Button size="xs" variant="subtle" colorPalette="orange" onClick={attach.onOpen}>
                                <UI.Icon name="attach"/> Anexar comprovante
                            </Button>
                        </UI.Show>

                        <UI.Show visible={isPix && hasReceipt}>
                            <Button size="xs" variant="subtle" onClick={() => window.open(data?.receipt?.download_url)}>
                                <UI.Icon name="attach"/> Ver comprovante
                            </Button>
                        </UI.Show>

                        <UI.Show visible={isPix && hasReceipt && isPending}>
                            <Button size="xs" variant="subtle" colorPalette="red" loading={deleting} onClick={handleDeleteReceipt}>
                                <UI.Icon name="trash"/> Excluir
                            </Button>
                        </UI.Show>

                        <UI.Show visible={isPending}>
                            <Button
                                size="xs"
                                colorPalette="green"
                                onClick={() => window.open(
                                    `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
                                        `Olá! Aqui é ${distributor.data?.name}. Preciso do link de pagamento referente ao pedido #${currentOrder?.cod}, no valor de R$ ${Mask.price(data?.amount)}.`
                                    )}`
                                )}
                            >
                                <UI.Icon name="whatsapp"/> Solicitar link de pagamento
                            </Button>
                        </UI.Show>

                    </HStack>
                </Table.Cell>
            </Table.Row>
            <UI.Show visible={isPix}><Attach data={data} onSuccess={invalidateOrder} {...attach} /></UI.Show>
            <UI.Show visible={isPix}><Pix data={data} {...pix} /></UI.Show>
        </>
    )
}
